import { useCallback, useEffect, useState } from "react";
import { Download, LogOut, Lock, Filter } from "lucide-react";
import { adminLogin, getStats, downloadCsv } from "../lib/api";
import { QUESTIONS, FRAGRANCES } from "../config/content";
import { Logo } from "../components/Logo";
import { FadeIn } from "../components/Reveal";

const TOKEN_KEY = "ldc_admin_token";

const Bar = ({ value, max, color = "bg-olive" }) => (
  <div className="h-1.5 w-full bg-surface">
    <div className={`h-1.5 ${color}`} style={{ width: `${max ? Math.round((value / max) * 100) : 0}%` }} />
  </div>
);

const Stat = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="border border-line px-5 py-4">
    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted2">{label}</p>
    <p className="mt-2 font-serif text-3xl text-ink">{value}</p>
    {sub && <p className="mt-1 font-mono text-[10px] text-gold">{sub}</p>}
  </div>
);

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", source: "", result: "" });

  const load = useCallback(
    async (t, f = filters) => {
      try {
        const res = await getStats(t || token, f);
        setStats(res.data);
      } catch {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
    },
    [token, filters]
  );

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filters]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await adminLogin(password);
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Accesso non riuscito.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStats(null);
  };

  if (!token) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-ink flex items-center justify-center px-6">
        <FadeIn className="w-full max-w-sm">
          <Logo light />
          <form onSubmit={login} className="mt-12">
            <label className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/60">
              <Lock className="w-3 h-3" /> Area riservata
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              placeholder="Password"
              className="mt-4 w-full bg-transparent border border-ivory/25 px-5 py-4 text-ivory placeholder:text-ivory/30 focus:outline-none focus:border-gold transition-colors"
            />
            {error && (
              <p data-testid="admin-login-error" className="mt-3 text-sm text-blush">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="mt-6 w-full bg-ivory text-ink px-5 py-4 font-mono text-xs tracking-[0.3em] transition-colors hover:bg-gold disabled:opacity-50"
            >
              ENTRA
            </button>
          </form>
        </FadeIn>
      </div>
    );
  }

  const total = stats?.completed || 0;
  const leads = stats?.leads || { total: 0, consents: 0, conversion_rate: 0, optin_rate: 0 };
  const maxDate = Math.max(1, ...Object.values(stats?.per_date || {}));
  const maxScore = Math.max(1, ...Object.values(stats?.score_avg || {}));
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ivory">
      <header className="bg-ink text-ivory px-6 pt-10 pb-8">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Logo light small />
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 text-ivory/60 transition-colors hover:text-ivory"
          >
            <LogOut className="w-4 h-4" />
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase">Esci</span>
          </button>
        </div>
        <div className="max-w-3xl mx-auto mt-10">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold">Dashboard</p>
          <h1 className="mt-2 font-serif text-3xl">L’esperienza in numeri</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-14 pb-20">
        <section data-testid="admin-filters" className="border border-line bg-surface/40 px-5 py-5">
          <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-muted2">
            <Filter className="w-3 h-3" /> Filtri
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              data-testid="filter-date-from"
              className="bg-transparent border border-line px-3 py-2.5 text-sm text-ink focus:outline-none focus:border-gold"
            />
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              data-testid="filter-date-to"
              className="bg-transparent border border-line px-3 py-2.5 text-sm text-ink focus:outline-none focus:border-gold"
            />
            <select
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              data-testid="filter-source"
              className="bg-transparent border border-line px-3 py-2.5 text-sm text-ink focus:outline-none focus:border-gold"
            >
              <option value="">Tutte le provenienze</option>
              {(stats?.available_sources || []).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              value={filters.result}
              onChange={(e) => setFilters({ ...filters, result: e.target.value })}
              data-testid="filter-result"
              className="bg-transparent border border-line px-3 py-2.5 text-sm text-ink focus:outline-none focus:border-gold"
            >
              <option value="">Tutte le fragranze</option>
              {FRAGRANCES.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          {hasFilters && (
            <button
              onClick={() => setFilters({ date_from: "", date_to: "", source: "", result: "" })}
              data-testid="filter-reset"
              className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-muted2 underline underline-offset-4 hover:text-ink"
            >
              Azzera filtri
            </button>
          )}
        </section>

        <section data-testid="admin-funnel-section">
          <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted2 mb-5">
            Funnel e traction
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Test iniziati" value={stats?.started ?? 0} testid="stat-started" />
            <Stat label="Test completati" value={stats?.completed ?? 0} sub={`${stats?.completion_rate ?? 0}% completion`} testid="stat-completed" />
            <Stat label="Email raccolte" value={leads.total} sub={`${leads.conversion_rate}% dei completati`} testid="stat-leads" />
            <Stat label="Consensi marketing" value={leads.consents} sub={`${leads.optin_rate}% opt-in`} testid="stat-consents" />
          </div>
        </section>

        <section data-testid="admin-results-section">
          <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted2 mb-6">
            Risultati per fragranza
          </h2>
          <div className="space-y-6">
            {FRAGRANCES.map((f, i) => {
              const count = stats?.results?.[f.id] || 0;
              const pct = total ? Math.round((count / total) * 100) : 0;
              return (
                <div key={f.id} data-testid={`admin-result-${f.id}`}>
                  <div className="flex items-baseline justify-between">
                    <span className="font-serif text-xl text-ink">{f.name}</span>
                    <span className="font-mono text-xs text-muted2">{count} · {pct}%</span>
                  </div>
                  <div className="mt-2">
                    <Bar value={count} max={total} color={["bg-olive", "bg-ink", "bg-gold"][i]} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section data-testid="admin-scores-section">
          <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted2 mb-6">
            Distribuzione dei punteggi interni (media per test completato)
          </h2>
          <div className="space-y-4">
            {FRAGRANCES.map((f, i) => (
              <div key={f.id} data-testid={`admin-score-${f.id}`}>
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-xs text-ink">score_{f.id}</span>
                  <span className="font-mono text-xs text-muted2">{stats?.score_avg?.[f.id] ?? 0}</span>
                </div>
                <div className="mt-1.5">
                  <Bar value={stats?.score_avg?.[f.id] ?? 0} max={maxScore} color={["bg-olive", "bg-ink", "bg-gold"][i]} />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="admin-questions-section">
          <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted2 mb-6">
            Distribuzione delle risposte
          </h2>
          <div className="space-y-8">
            {QUESTIONS.map((q) => {
              const dist = stats?.per_question?.[String(q.id)] || { A: 0, B: 0, C: 0 };
              const qTotal = Math.max(1, dist.A + dist.B + dist.C);
              return (
                <div key={q.id} className="border-t border-line pt-5">
                  <p className="font-serif text-lg text-ink leading-snug">
                    <span className="font-mono text-xs text-gold mr-2">{String(q.id).padStart(2, "0")}</span>
                    {q.text}
                  </p>
                  <div className="mt-4 space-y-3">
                    {q.options.map((opt) => {
                      const c = dist[opt.letter] || 0;
                      return (
                        <div key={opt.letter} data-testid={`admin-q${q.id}-${opt.letter}`}>
                          <div className="flex items-baseline justify-between gap-4">
                            <span className="text-sm text-muted2 flex-1">
                              <span className="font-mono text-xs text-gold mr-2">{opt.letter}</span>
                              {opt.text}
                            </span>
                            <span className="font-mono text-xs text-ink whitespace-nowrap">
                              {c} · {Math.round((c / qTotal) * 100)}%
                            </span>
                          </div>
                          <div className="mt-1.5">
                            <Bar value={c} max={qTotal} color="bg-olive-light" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section data-testid="admin-dates-section">
          <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted2 mb-6">
            Test completati per data
          </h2>
          <div className="space-y-3">
            {Object.entries(stats?.per_date || {}).length === 0 && (
              <p className="text-sm text-muted2">Nessun test nel periodo selezionato.</p>
            )}
            {Object.entries(stats?.per_date || {}).map(([day, count]) => (
              <div key={day} data-testid={`admin-date-${day}`}>
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-xs text-ink">{day}</span>
                  <span className="font-mono text-xs text-muted2">{count}</span>
                </div>
                <div className="mt-1.5">
                  <Bar value={count} max={maxDate} color="bg-gold" />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="admin-sources-section">
          <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted2 mb-6">
            Provenienza (source)
          </h2>
          <div className="space-y-3">
            {Object.entries(stats?.sources || {}).length === 0 && (
              <p className="text-sm text-muted2">Nessuna provenienza registrata.</p>
            )}
            {Object.entries(stats?.sources || {}).map(([src, d]) => (
              <div key={src} data-testid={`admin-source-${src}`} className="border-t border-line pt-3">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-xs text-ink">{src}</span>
                  <span className="font-mono text-[11px] text-muted2">
                    {d.started} iniziati · {d.completed} completati · {d.leads} email · {d.consents} consensi
                  </span>
                </div>
                <div className="mt-1.5">
                  <Bar value={d.completed} max={total || 1} color="bg-olive" />
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="space-y-3">
          <button
            onClick={() => downloadCsv(token, filters, "sessions")}
            data-testid="export-csv-button"
            className="flex w-full items-center justify-between border border-ink px-6 py-5 text-ink transition-colors duration-500 hover:bg-ink hover:text-ivory"
          >
            <span className="font-mono text-xs tracking-[0.3em]">ESPORTA SESSIONI CSV</span>
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={() => downloadCsv(token, filters, "leads")}
            data-testid="export-leads-csv-button"
            className="flex w-full items-center justify-between border border-ink px-6 py-5 text-ink transition-colors duration-500 hover:bg-ink hover:text-ivory"
          >
            <span className="font-mono text-xs tracking-[0.3em]">ESPORTA LEAD CSV</span>
            <Download className="w-4 h-4" />
          </button>
        </div>
      </main>
    </div>
  );
}
